import { roundedClassname, roundedDropdownClassname } from '@/helpers/environmentHelper';
import { useAppDispatch } from '@/state/hooks';
import { setShowOverlay } from '@/state/reducers/generalSlice';
import {
    addXplorSpotWorkspaceTab,
    addXplorTraderWorkspaceTab,
    deleteXplorSpotWorkspaceTab,
    deleteXplorTraderWorkspaceTab,
    renameXplorSpotWorkspaceTab,
    renameXplorTraderWorkspaceTab,
    setXplorSpotWorkspace,
    setXplorSpotWorkspaceTabLayout,
    setXplorTraderWorkspace,
    setXplorTraderWorkspaceTabLayout
} from '@/state/reducers/workspaceSlice';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import { defaultXplorSpotLayout } from '@/utils/hooks/useXplorSpotLayout';
import { defaultXplorTraderLayout } from '@/utils/hooks/useXplorTraderLayout';
import { SSOLoadingState } from '@/utils/providers/SSOProvider';
import { Menu, Transition } from '@headlessui/react';
import cn from 'classnames';
import { ButtonHTMLAttributes, Fragment, ReactNode, useMemo, useState } from 'react';
import { IconType } from 'react-icons/lib';
import { LuCheck, LuPen, LuPlus, LuTrash2, LuX } from 'react-icons/lu';
import { MdRefresh } from 'react-icons/md';
import Divider from '../common/Divider';
import { Portal } from '../common/Portal';

interface IWorkspaceDropdown {
    label?: string;
    button: ReactNode;
    workspaces: any[];
    className?: string;
    position?: 'left' | 'right';
    children: ReactNode;
}

export const WorkspaceDropdown = ({
    label,
    button,
    workspaces,
    className,
    position = 'right',
    children
}: IWorkspaceDropdown) => {
    const pathname = window.location.pathname;
    const dispatch = useAppDispatch();

    const lang = useLanguage();

    return (
        <div className={cn(`flex z-[51]`, className)}>
            <Menu
                as="div"
                className={cn(
                    'relative inline-block text-left border border-brand-neutral-700 hover:border-brand-neutral-600 transition duration-150 bg-brand-background',
                    roundedClassname
                )}>
                {({ open }) => {
                    useDidUpdate(() => {
                        dispatch(setShowOverlay(open));
                    }, [open]);
                    return (
                        <>
                            <div className="relative flex items-center">
                                <span className="pointer-events-none absolute -top-[8.5px] left-0.5 z-10 px-1.5 text-2xs font-bold text-brand-neutral-400 after:content-[''] after:absolute after:left-[2px] after:right-[2px] after:top-1/2 after:h-[1px] after:bg-brand-background after:-z-10">
                                    {label}
                                </span>
                                {button}
                            </div>
                            <Portal className="absolute z-[100] top-12 left-[73px]">
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95">
                                    <div
                                        className={cn(
                                            'flex flex-col absolute mt-2 min-w-52 max-w-56 origin-top-right p-1 bg-brand-background-dark border border-brand-neutral-700 overflow-hidden text-brand-text',
                                            { [`${position}-0`]: true },
                                            roundedDropdownClassname
                                        )}>
                                        <div className="p-2 text-xs text-brand-neutral-400">
                                            {lang.workspace.yourLayouts}
                                        </div>
                                        <Menu.Items className="flex flex-col gap-1">
                                            {children}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (pathname.includes('/trader')) {
                                                        const layout = defaultXplorTraderLayout();
                                                        dispatch(
                                                            setXplorTraderWorkspaceTabLayout({
                                                                id: 'current',
                                                                layout: layout.desktop,
                                                                screen: 'desktop'
                                                            })
                                                        );
                                                    } else {
                                                        const layout = defaultXplorSpotLayout();
                                                        dispatch(
                                                            setXplorSpotWorkspaceTabLayout({
                                                                id: 'current',
                                                                layout: layout.desktop,
                                                                screen: 'desktop'
                                                            })
                                                        );
                                                    }
                                                }}
                                                className="my-0.5 group relative flex items-center border border-dashed border-brand-neutral-600 hover:border-brand-neutral-500 text-brand-neutral-400 hover:text-brand-text gap-1 px-1.5 py-1.5 text-xs rounded-md transition-colors duration-150"
                                                title="Reset Layout">
                                                <MdRefresh className="min-w-3.5 min-h-3.5" />
                                                Reset workspace
                                                {/* {pathname.includes('/trader')
                                                    ? `(${xplorTraderTitle})`
                                                    : `(${xplorSpotTitle})`} */}
                                                <div className="group-hover:bg-brand-neutral-500 opacity-10 absolute h-full w-full inset-0 rounded-md transition-colors duration-150" />
                                            </button>
                                            <Divider className="mx-1" />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (pathname.includes('/trader')) {
                                                        dispatch(
                                                            addXplorTraderWorkspaceTab({
                                                                name: `Workspace ${workspaces.length + 1}`
                                                            })
                                                        );
                                                    } else {
                                                        dispatch(
                                                            addXplorSpotWorkspaceTab({
                                                                name: `Workspace ${workspaces.length + 1}`
                                                            })
                                                        );
                                                    }
                                                }}
                                                className="flex gap-1 px-1.5 py-2 text-sm items-center text-brand-neutral-400 hover:bg-brand-neutral-700 hover:text-brand-text rounded-md transition-colors duration-150"
                                                title="Add Workspace">
                                                <LuPlus className="min-w-4 min-h-4" />
                                                {lang.workspace.createNewLayout}
                                            </button>
                                        </Menu.Items>
                                    </div>
                                </Transition>
                            </Portal>
                        </>
                    );
                }}
            </Menu>
        </div>
    );
};

interface MenuItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    id: string;
    Icon?: IconType;
    icon?: any;
    label: string;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
    isActive?: boolean;
    isOnlyWorkspace?: boolean;
    isDefault?: boolean;
}

export const MenuItemButton = (props: MenuItemButtonProps) => {
    const {
        id,
        icon,
        Icon,
        label,
        disabledMessage,
        isLoadingSSO = 'idle',
        requiresSSO = false,
        isActive,
        isOnlyWorkspace,
        isDefault,
        ...restProps
    } = props;
    const { disabled } = restProps;

    const pathname = location.pathname;
    const dispatch = useAppDispatch();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editName, setEditName] = useState(label);

    const saveEditing = () => {
        if (id && editName.trim()) {
            if (pathname.includes('/trader')) {
                dispatch(renameXplorTraderWorkspaceTab({ id, name: editName.trim() }));
                if (isActive) dispatch(setXplorTraderWorkspace(id));
            } else {
                dispatch(renameXplorSpotWorkspaceTab({ id, name: editName.trim() }));
                if (isActive) dispatch(setXplorSpotWorkspace(id));
            }
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') saveEditing();
        if (e.key === 'Escape') setIsEditing(false);
    };

    const ssoTooltip = useMemo(() => {
        if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
        if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
        return '';
    }, [isLoadingSSO]);

    const tooltip = useMemo(() => {
        if (disabled) return ssoTooltip || disabledMessage;
        return ssoTooltip;
    }, [disabled, disabledMessage, ssoTooltip]);

    return (
        <Menu.Item>
            <button
                type="button"
                title={tooltip}
                className={cn(
                    'group flex w-full gap-2 items-center p-2 text-sm rounded-md text-brand-text hover:bg-brand-neutral-700 hover:text-brand-module-title focus-visible:outline-none',
                    {
                        'bg-brand-neutral-700': isActive,
                        'bg-brand-background-dark': !isActive
                    }
                )}
                {...restProps}>
                {Icon ? (
                    <span>
                        <Icon className="h-5 w-5" />
                    </span>
                ) : null}
                {icon ? <span>{icon}</span> : null}
                {isDefault ? (
                    <div className="text-left line-clamp-1">
                        {label} <span className="text-brand-neutral-400 text-xs">(Default)</span>
                    </div>
                ) : (
                    <Fragment>
                        {isEditing ? (
                            <div className="flex items-center justify-between w-full gap-1">
                                <input
                                    autoFocus
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onBlur={saveEditing}
                                    onKeyDown={handleKeyDown}
                                    onClick={(e) => e.stopPropagation()}
                                    className="bg-transparent text-brand-module-title text-xs rounded outline-none"
                                />
                                <div className="flex gap-2 ml-auto text-brand-text">
                                    <div
                                        className="hover:text-brand-green-light transition-all duration-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            saveEditing();
                                        }}>
                                        <LuCheck />
                                    </div>
                                    <div
                                        className="hover:text-brand-red-light transition-all duration-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditing(false);
                                        }}>
                                        <LuX />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Fragment>
                                <div className="text-left line-clamp-1">{label}</div>
                                <div className="flex gap-2 group-hover:opacity-100 opacity-0 ml-auto text-brand-neutral-400">
                                    <div
                                        className="hover:text-brand-text transition-all duration-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditing(true);
                                            setEditName(label);
                                        }}>
                                        <LuPen />
                                    </div>
                                    {!isOnlyWorkspace && (
                                        <div
                                            className="hover:text-brand-red-light transition-all duration-100"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (pathname.includes('/trader')) {
                                                    dispatch(deleteXplorTraderWorkspaceTab({ id }));
                                                } else {
                                                    dispatch(deleteXplorSpotWorkspaceTab({ id }));
                                                }
                                            }}>
                                            <LuTrash2 />
                                        </div>
                                    )}
                                </div>
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </button>
        </Menu.Item>
    );
};
